"use client";

import {
    ChevronLeft,
    ChevronRight,
    Home,
    Search,
    Clock,
    Calendar,
    BookOpen,
    ArrowUpRight,
    Sparkles,
    CheckCircle2,
    X,
    Cpu,
    Compass,
    Wrench,
    BarChart3,
    Laptop,
    ThermometerSnowflake,
    Building2,
    HelpCircle,
    LayoutGrid,
    SlidersHorizontal,
    Tag,
    RotateCcw,
    PhoneCall,
    Filter
} from "lucide-react";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { isRTL, type Locale, localePath } from "@/lib/i18n";
import { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { getArticles } from "@/lib/articles";

type Props = {
    locale: Locale;
};

// UI chrome text for this page — kept separate from article content
// (lib/articles.ts) since it's fixed strings, not per-article data.
// Every locale gets its own real translation; `en` is the fallback for
// any locale without a dedicated entry below.
const UI_STRINGS = {
    fa: {
        expertCenter: "مرجع مقالات تخصصی",
        searchPlaceholder: "جستجو در موضوعات، مقالات و راهنماهای تلماتیک...",
        articlesAvailable: "مقاله تخصصی",
        filterCategories: "فیلتر و دسته‌بندی موضوعات",
        closeMenu: "بستن منو ▲",
        expandMenu: "مشاهده منو ▼",
        categories: "دسته‌بندی موضوعات",
        resetAllFiltersTitle: "بازنشانی همه فیلترها",
        resetShort: "همه",
        allArticles: "همه مقالات",
        popularTopics: "برچسب‌های پرکاربرد",
        needGuidanceTitle: "نیاز به مشاوره فنی دارید؟",
        needGuidanceBody: "کارشناسان فرجاد سیستم برای انتخاب بهترین سخت‌افزار و پلتفرم در کنار شما هستند.",
        contactSpecialists: "تماس با کارشناسان",
        allEducationalGuides: "تمامی مقالات و راهنماهای تخصصی",
        showingArticles: (n: number) => `نمایش ${n} مقاله`,
        searchLabel: "جستجو:",
        clearAll: "پاک کردن همه",
        readArticle: "مطالعه مقاله",
        noArticlesFound: "مقاله‌ای یافت نشد",
        noArticlesBody: "عبارت دیگری را جستجو کرده یا فیلتر دسته‌بندی را تغییر دهید.",
        clearFilters: "پاک کردن فیلترها",
        quote: "دانش، قدرت است. ما در فرجاد باور داریم که آگاهی از فناوری، اولین و استوارترین گام در مسیر هوشمندسازی و تحول ناوگان شماست.",
        rndTeam: "تیم تحقیق و توسعه فرجاد سیستم پرداز"
    },
    ar: {
        expertCenter: "مرجع المقالات المتخصصة",
        searchPlaceholder: "ابحث في المواضيع والمقالات وأدلة التلماتيات...",
        articlesAvailable: "مقالة متاحة",
        filterCategories: "التصفية والتصنيفات",
        closeMenu: "إغلاق ▲",
        expandMenu: "عرض ▼",
        categories: "التصنيفات",
        resetAllFiltersTitle: "إعادة تعيين كل الفلاتر",
        resetShort: "إعادة تعيين",
        allArticles: "جميع المقالات",
        popularTopics: "الوسوم الأكثر استخدامًا",
        needGuidanceTitle: "هل تحتاج إلى استشارة فنية؟",
        needGuidanceBody: "خبراء فرجاد سيستم جاهزون لمساعدتك في اختيار أفضل عتاد ومنصة.",
        contactSpecialists: "تواصل مع الخبراء",
        allEducationalGuides: "جميع المقالات والأدلة المتخصصة",
        showingArticles: (n: number) => `عرض ${n} مقالة`,
        searchLabel: "بحث:",
        clearAll: "مسح الكل",
        readArticle: "قراءة المقال",
        noArticlesFound: "لم يتم العثور على مقالات",
        noArticlesBody: "جرّب البحث بعبارة أخرى أو غيّر فلتر التصنيف.",
        clearFilters: "مسح الفلاتر",
        quote: "المعرفة قوة. في فرجاد، نؤمن بأن الوعي التقني هو الخطوة الأولى والأكثر رسوخًا نحو أتمتة أسطولك وتحوله الذكي.",
        rndTeam: "فريق البحث والتطوير في فرجاد سيستم"
    },
    de: {
        expertCenter: "Experten-Wissenszentrum",
        searchPlaceholder: "Themen, Artikel und Telematik-Leitfäden durchsuchen...",
        articlesAvailable: "Artikel verfügbar",
        filterCategories: "Filter & Kategorien",
        closeMenu: "Schließen ▲",
        expandMenu: "Erweitern ▼",
        categories: "Kategorien",
        resetAllFiltersTitle: "Alle Filter zurücksetzen",
        resetShort: "Zurücksetzen",
        allArticles: "Alle Artikel",
        popularTopics: "Beliebte Themen",
        needGuidanceTitle: "Benötigen Sie technische Beratung?",
        needGuidanceBody: "Unsere Experten helfen Ihnen gerne bei der Auswahl der idealen Tracking-Lösung.",
        contactSpecialists: "Experten kontaktieren",
        allEducationalGuides: "Alle Leitfäden & Ratgeber",
        showingArticles: (n: number) => `${n} Artikel werden angezeigt`,
        searchLabel: "Suche:",
        clearAll: "Alle löschen",
        readArticle: "Artikel lesen",
        noArticlesFound: "Keine Artikel gefunden",
        noArticlesBody: "Versuchen Sie andere Suchbegriffe oder setzen Sie die Kategoriefilter zurück.",
        clearFilters: "Filter zurücksetzen",
        quote: "Wissen ist Macht. Bei Farjad glauben wir, dass technisches Bewusstsein der grundlegende erste Schritt zur intelligenten Transformation Ihrer Flotte ist.",
        rndTeam: "Farjad F&E-Team"
    },
    tr: {
        expertCenter: "Uzman Bilgi Merkezi",
        searchPlaceholder: "Konularda, makalelerde ve telematik rehberlerinde ara...",
        articlesAvailable: "makale mevcut",
        filterCategories: "Filtre ve Kategoriler",
        closeMenu: "Kapat ▲",
        expandMenu: "Genişlet ▼",
        categories: "Kategoriler",
        resetAllFiltersTitle: "Tüm filtreleri sıfırla",
        resetShort: "Sıfırla",
        allArticles: "Tüm Makaleler",
        popularTopics: "Popüler Konular",
        needGuidanceTitle: "Teknik Rehberliğe mi İhtiyacınız Var?",
        needGuidanceBody: "Uzmanlarımız ideal takip çözümünü seçmenize yardımcı olmaya hazır.",
        contactSpecialists: "Uzmanlarla İletişime Geçin",
        allEducationalGuides: "Tüm Eğitim Rehberleri",
        showingArticles: (n: number) => `${n} makale gösteriliyor`,
        searchLabel: "Arama:",
        clearAll: "Tümünü Temizle",
        readArticle: "Makaleyi Oku",
        noArticlesFound: "Makale bulunamadı",
        noArticlesBody: "Farklı arama terimleri deneyin veya kategori filtrelerinizi temizleyin.",
        clearFilters: "Filtreleri Temizle",
        quote: "Bilgi güçtür. Farjad olarak, teknoloji farkındalığının akıllı filo dönüşümüne giden en sağlam ilk adım olduğuna inanıyoruz.",
        rndTeam: "Farjad Ar-Ge Ekibi"
    },
    en: {
        expertCenter: "Expert Knowledge Center",
        searchPlaceholder: "Search articles, telematics topics, and guides...",
        articlesAvailable: "Articles available",
        filterCategories: "Filter & Categories",
        closeMenu: "Close ▲",
        expandMenu: "Expand ▼",
        categories: "Categories",
        resetAllFiltersTitle: "Reset Filters",
        resetShort: "Reset",
        allArticles: "All Articles",
        popularTopics: "Popular Topics",
        needGuidanceTitle: "Need Technical Guidance?",
        needGuidanceBody: "Our experts are ready to assist you in selecting the ideal tracking solution.",
        contactSpecialists: "Contact Specialists",
        allEducationalGuides: "All Educational Guides",
        showingArticles: (n: number) => `Showing ${n} articles`,
        searchLabel: "Search:",
        clearAll: "Clear All",
        readArticle: "Read Article",
        noArticlesFound: "No articles found",
        noArticlesBody: "Try adjusting your search terms or clearing your category filters.",
        clearFilters: "Clear Filters",
        quote: "Knowledge is power. At Farjad, we believe technology awareness is the fundamental first step towards intelligent fleet transformation.",
        rndTeam: "Farjad R&D Team"
    }
} satisfies Record<string, Record<string, string | ((n: number) => string)>>;

export default function KnowledgeBaseContent({ locale }: Props) {
    const isRtl = isRTL(locale);
    const t = useTranslations("general.knowledge-base");
    const tNav = useTranslations("nav");
    const ui = UI_STRINGS[locale as keyof typeof UI_STRINGS] ?? UI_STRINGS.en;

    const allArticles = useMemo(() => getArticles(locale), [locale]);

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<string>("ALL");
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

    // Extract unique categories
    const categories = useMemo(() => {
        const set = new Set<string>();
        allArticles.forEach((a) => set.add(a.category));
        return Array.from(set);
    }, [allArticles]);

    // Quick popular search tags — query strings must match real words in
    // that locale's article content (lib/articles.ts) for the filter to work.
    const popularTags = useMemo(() => {
        switch (locale) {
            case "fa":
                return [
                    { label: "GPS چیست", query: "GPS" },
                    { label: "ردیاب آهنربایی", query: "آهنربایی" },
                    { label: "خاموش کردن خودرو", query: "خاموش کردن" },
                    { label: "دزدگیر و ردیاب", query: "دزدگیر" },
                    { label: "سامانه مدیریت ناوگان", query: "مدیریت ناوگان" },
                    { label: "سنسور باک سوخت", query: "سوخت" },
                    { label: "نگهداری و تعمیرات نت", query: "تعمیرات" },
                    { label: "راهنمای نصب", query: "نصب" },
                    { label: "زنجیره سرد", query: "زنجیره سرد" },
                    { label: "عیب‌یابی ردیاب", query: "عیب‌یابی" }
                ];
            case "ar":
                return [
                    { label: "ما هو GPS", query: "GPS" },
                    { label: "إنذار مقابل جهاز تتبع", query: "إنذار" },
                    { label: "دليل التركيب", query: "تركيب" },
                    { label: "خفض تكاليف الوقود", query: "وقود" },
                    { label: "سلسلة التبريد", query: "سلسلة التبريد" },
                    { label: "أسطول التوزيع", query: "التوزيع" },
                    { label: "استكشاف الأخطاء", query: "استكشاف الأخطاء" }
                ];
            case "de":
                return [
                    { label: "Was ist GPS", query: "GPS" },
                    { label: "Autoalarm vs. Tracker", query: "Alarm" },
                    { label: "Installationsanleitung", query: "Installation" },
                    { label: "Kraftstoffkosten senken", query: "Kraftstoff" },
                    { label: "Kühlkette", query: "Kühlkette" },
                    { label: "Verteilerflotten", query: "Verteilerflotte" },
                    { label: "Fehlerbehebung", query: "Fehlerbehebung" }
                ];
            case "tr":
                return [
                    { label: "GPS Nedir", query: "GPS" },
                    { label: "Araç Alarmı vs Takip Cihazı", query: "Alarmı" },
                    { label: "Kurulum Rehberi", query: "Kurulum" },
                    { label: "Yakıt Maliyetlerini Düşürme", query: "Yakıt" },
                    { label: "Soğuk Zincir", query: "Soğuk Zincir" },
                    { label: "Dağıtım Filoları", query: "Dağıtım" },
                    { label: "Sorun Giderme", query: "Sorun Giderme" }
                ];
            default:
                return [
                    { label: "What is GPS", query: "GPS" },
                    { label: "Car Alarm vs Tracker", query: "Alarm" },
                    { label: "Installation Guide", query: "Installation" },
                    { label: "Fuel Reduction", query: "Fuel" },
                    { label: "Cold Chain", query: "Cold Chain" },
                    { label: "Distribution Fleet", query: "Distribution" },
                    { label: "Troubleshooting", query: "Troubleshooting" }
                ];
        }
    }, [locale]);

    // Helper to get category icon
    const getCategoryIcon = (category: string, isSelected: boolean) => {
        const iconClass = isSelected
            ? "text-white"
            : "text-brand-orange transition-colors";
        const size = 18;

        switch (category) {
            case "ALL":
                return <LayoutGrid size={size} className={iconClass} />;
            case "مفاهیم پایه و فناوری":
            case "Fundamentals & Tech":
                return <Cpu size={size} className={iconClass} />;
            case "راهنمای خرید":
            case "Buying Guide":
                return <Compass size={size} className={iconClass} />;
            case "راهنمای فنی":
            case "Technical & Installation":
                return <Wrench size={size} className={iconClass} />;
            case "مدیریت ناوگان":
            case "Fleet Management":
                return <BarChart3 size={size} className={iconClass} />;
            case "فناوری و نرم‌افزار":
            case "Software & Technology":
                return <Laptop size={size} className={iconClass} />;
            case "راهکار تخصصی":
            case "Specialized Solutions":
                return <ThermometerSnowflake size={size} className={iconClass} />;
            case "راهکار سازمانی":
            case "Enterprise Solutions":
                return <Building2 size={size} className={iconClass} />;
            case "پشتیبانی و عیب‌یابی":
            case "Support & Troubleshooting":
                return <HelpCircle size={size} className={iconClass} />;
            default:
                return <BookOpen size={size} className={iconClass} />;
        }
    };

    // Filter articles based on category and search query
    const filteredArticles = useMemo(() => {
        return allArticles.filter((article) => {
            const matchesCategory =
                selectedCategory === "ALL" || article.category === selectedCategory;

            if (!matchesCategory) return false;

            if (!searchQuery.trim()) return true;

            const q = searchQuery.toLowerCase().trim();
            const titleMatch = article.title.toLowerCase().includes(q);
            const excerptMatch = article.excerpt.toLowerCase().includes(q);
            const categoryMatch = article.category.toLowerCase().includes(q);
            const keywordsMatch = article.keywords.toLowerCase().includes(q);
            const bodyMatch = article.sections.some(
                (s) =>
                    s.heading.toLowerCase().includes(q) ||
                    s.body.some((p) => p.toLowerCase().includes(q))
            );

            return titleMatch || excerptMatch || categoryMatch || keywordsMatch || bodyMatch;
        });
    }, [allArticles, selectedCategory, searchQuery]);

    const isFiltered = selectedCategory !== "ALL" || searchQuery.trim().length > 0;

    const resetFilters = () => {
        setSelectedCategory("ALL");
        setSearchQuery("");
    };

    return (
        <div className={`min-h-screen flex flex-col bg-slate-50/60 ${isRtl ? "rtl text-right" : "ltr text-left"}`}>
            <main className="flex-grow">
                {/* Hero / Header Section */}
                <section className="bg-brand-blue-gray pt-16 pb-24 lg:pt-20 lg:pb-32 text-white relative overflow-hidden">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center max-w-3xl mx-auto">
                            {/* Eyebrow Badge */}
                            <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md shadow-lg shadow-black/5 mb-6 text-white text-xs sm:text-sm font-semibold select-none group transition-all duration-300 hover:bg-white/15 hover:border-brand-orange/40">
                                <span className="flex h-2 w-2 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-orange opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-orange"></span>
                                </span>
                                <span className="font-bold text-white tracking-wide">{t("title")}</span>
                                <span className="text-white/40">|</span>
                                <span className="text-orange-200 text-xs font-normal flex items-center gap-1">
                                    <Sparkles size={13} className="text-brand-orange" />
                                    <span>{ui.expertCenter}</span>
                                </span>
                            </div>

                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-5 leading-tight tracking-tight">
                                {t("hero.title")}
                            </h1>
                            <p className="text-base sm:text-lg text-blue-100/90 mb-8 font-normal leading-relaxed">
                                {t("hero.desc")}
                            </p>

                            {/* Search Input Bar */}
                            <div className="max-w-2xl mx-auto relative group">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={ui.searchPlaceholder}
                                    className={`w-full bg-white/10 border border-white/20 rounded-2xl py-4 ${
                                        isRtl ? "pr-13 pl-12" : "pl-13 pr-12"
                                    } text-white placeholder-blue-200/60 backdrop-blur-md focus:bg-white focus:text-gray-900 focus:placeholder-gray-400 transition-all text-base shadow-xl`}
                                />
                                <Search
                                    className={`absolute ${
                                        isRtl ? "right-4.5" : "left-4.5"
                                    } top-1/2 -translate-y-1/2 text-blue-200 group-focus-within:text-[var(--sm-teal-600)] transition-colors`}
                                    size={20}
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className={`absolute ${
                                            isRtl ? "left-4" : "right-4"
                                        } top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-white/20 text-white group-focus-within:text-gray-500 transition-colors`}
                                        aria-label="Clear search"
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-brand-orange/15 to-transparent pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-brand-orange/10 rounded-full blur-3xl pointer-events-none" />
                </section>

                {/* Breadcrumb Bar */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-7 relative z-20">
                    <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 p-4 sm:p-4.5 border border-slate-100 flex items-center justify-between">
                        <Breadcrumb dir={isRtl ? "rtl" : "ltr"}>
                            <BreadcrumbList className="flex items-center gap-2 text-sm">
                                <BreadcrumbItem>
                                    <BreadcrumbLink
                                        href={localePath(locale)}
                                        className="text-slate-500 hover:text-brand-orange transition-colors flex items-center gap-1"
                                        aria-label={tNav("home")}
                                    >
                                        <Home size={16} />
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator className="text-slate-300">
                                    {isRtl ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
                                </BreadcrumbSeparator>
                                <BreadcrumbItem>
                                    <BreadcrumbPage className="font-semibold text-slate-800">
                                        {t("title")}
                                    </BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>

                        <div className="hidden sm:flex items-center gap-2 text-xs font-semibold text-slate-500 bg-slate-100 px-3.5 py-1.5 rounded-full">
                            <BookOpen size={14} className="text-brand-orange" />
                            <span>
                                {filteredArticles.length}{" "}
                                {ui.articlesAvailable}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Main Two-Column Layout (Sidebar + Article Grid) */}
                <section className="py-10 sm:py-14 lg:py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Mobile Filter Toggle Button */}
                        <div className="lg:hidden mb-6">
                            <button
                                onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
                                className="w-full flex items-center justify-between bg-white border border-slate-200/80 p-4 rounded-2xl shadow-xs font-bold text-sm text-slate-800 hover:border-brand-orange transition-colors"
                            >
                                <div className="flex items-center gap-2.5">
                                    <Filter size={18} className="text-brand-orange" />
                                    <span>{ui.filterCategories}</span>
                                    {selectedCategory !== "ALL" && (
                                        <span className="px-2 py-0.5 bg-brand-orange text-white rounded-full text-xs font-semibold">
                                            {selectedCategory}
                                        </span>
                                    )}
                                </div>
                                <span className="text-xs text-brand-orange font-semibold">
                                    {isMobileFilterOpen ? ui.closeMenu : ui.expandMenu}
                                </span>
                            </button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                            {/* Sticky Sidebar Column */}
                            <aside
                                className={`lg:col-span-4 xl:col-span-3.5 space-y-6 ${
                                    isMobileFilterOpen ? "block" : "hidden lg:block"
                                } lg:sticky lg:top-24`}
                            >
                                {/* Category Navigation Widget */}
                                <div className="bg-white rounded-3xl p-5 sm:p-6 border border-slate-200/80 shadow-md shadow-slate-200/40">
                                    <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-4">
                                        <div className="flex items-center gap-2.5 font-extrabold text-base text-slate-900">
                                            <SlidersHorizontal size={18} className="text-brand-orange" />
                                            <span>{ui.categories}</span>
                                        </div>
                                        {isFiltered && (
                                            <button
                                                onClick={resetFilters}
                                                className="text-xs font-bold text-slate-400 hover:text-brand-orange transition-colors flex items-center gap-1 cursor-pointer"
                                                title={ui.resetAllFiltersTitle}
                                            >
                                                <RotateCcw size={12} />
                                                <span>{ui.resetShort}</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Category Item List */}
                                    <nav className="space-y-1.5" aria-label="Knowledge Base Categories">
                                        {/* All Articles */}
                                        <button
                                            onClick={() => {
                                                setSelectedCategory("ALL");
                                                setIsMobileFilterOpen(false);
                                            }}
                                            className={`w-full flex items-center justify-between px-3.5 py-3 rounded-2xl text-xs sm:text-sm font-bold transition-all duration-200 cursor-pointer ${
                                                selectedCategory === "ALL"
                                                    ? "bg-brand-orange text-white shadow-md shadow-brand-orange/25"
                                                    : "text-slate-700 hover:bg-orange-50/70 hover:text-brand-orange"
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="shrink-0">{getCategoryIcon("ALL", selectedCategory === "ALL")}</span>
                                                <span>{ui.allArticles}</span>
                                            </div>
                                            <span
                                                className={`px-2 py-0.5 rounded-full text-[11px] font-extrabold ${
                                                    selectedCategory === "ALL"
                                                        ? "bg-white/25 text-white"
                                                        : "bg-slate-100 text-slate-600"
                                                }`}
                                            >
                                                {allArticles.length}
                                            </span>
                                        </button>

                                        {/* Specific Categories */}
                                        {categories.map((cat) => {
                                            const count = allArticles.filter((a) => a.category === cat).length;
                                            const isSelected = selectedCategory === cat;
                                            return (
                                                <button
                                                    key={cat}
                                                    onClick={() => {
                                                        setSelectedCategory(cat);
                                                        setIsMobileFilterOpen(false);
                                                    }}
                                                    className={`w-full flex items-center justify-between px-3.5 py-3 rounded-2xl text-xs sm:text-sm font-bold transition-all duration-200 cursor-pointer ${
                                                        isSelected
                                                            ? "bg-brand-orange text-white shadow-md shadow-brand-orange/25"
                                                            : "text-slate-700 hover:bg-orange-50/70 hover:text-brand-orange"
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <span className="shrink-0">{getCategoryIcon(cat, isSelected)}</span>
                                                        <span>{cat}</span>
                                                    </div>
                                                    <span
                                                        className={`px-2 py-0.5 rounded-full text-[11px] font-extrabold ${
                                                            isSelected
                                                                ? "bg-white/25 text-white"
                                                                : "bg-slate-100 text-slate-600"
                                                        }`}
                                                    >
                                                        {count}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </nav>
                                </div>

                                {/* Popular Topic Tags Widget */}
                                <div className="bg-white rounded-3xl p-5 sm:p-6 border border-slate-200/80 shadow-md shadow-slate-200/40">
                                    <div className="flex items-center gap-2 font-bold text-sm text-slate-900 pb-3 border-b border-slate-100 mb-4">
                                        <Tag size={15} className="text-brand-orange" />
                                        <span>{ui.popularTopics}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {popularTags.map((tag, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => {
                                                    setSearchQuery(tag.query);
                                                    setIsMobileFilterOpen(false);
                                                }}
                                                className={`text-xs px-3 py-1.5 rounded-xl font-medium transition-colors cursor-pointer ${
                                                    searchQuery === tag.query
                                                        ? "bg-orange-100 text-brand-orange font-bold border border-orange-200"
                                                        : "bg-slate-100/80 hover:bg-orange-50 text-slate-600 hover:text-brand-orange border border-transparent hover:border-orange-200/60"
                                                }`}
                                            >
                                                #{tag.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Quick Help & Expert Consultation Card */}
                                <div className="bg-gradient-to-br from-brand-blue-gray to-slate-900 text-white rounded-3xl p-6 shadow-xl relative overflow-hidden">
                                    <div className="relative z-10">
                                        <div className="w-10 h-10 rounded-2xl bg-brand-orange/20 border border-brand-orange/40 flex items-center justify-center text-brand-orange mb-4">
                                            <PhoneCall size={20} />
                                        </div>
                                        <h3 className="font-extrabold text-base mb-2">
                                            {ui.needGuidanceTitle}
                                        </h3>
                                        <p className="text-xs text-slate-300 leading-relaxed mb-5">
                                            {ui.needGuidanceBody}
                                        </p>
                                        <a
                                            href={localePath(locale, "contactus")}
                                            className="inline-flex items-center justify-center w-full bg-brand-orange text-white py-2.5 rounded-xl text-xs font-bold hover:bg-orange-600 transition-colors shadow-md"
                                        >
                                            {ui.contactSpecialists}
                                        </a>
                                    </div>
                                    <div className="absolute -bottom-8 -right-8 w-28 h-28 bg-brand-orange/15 rounded-full blur-xl pointer-events-none" />
                                </div>
                            </aside>

                            {/* Main Content Area */}
                            <div className="lg:col-span-8 xl:col-span-8.5 space-y-6">
                                {/* Feed Header Status Bar */}
                                <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2.5 h-6 bg-brand-orange rounded-full hidden sm:block" />
                                        <div>
                                            <h2 className="text-base sm:text-lg font-extrabold text-slate-900 leading-tight">
                                                {selectedCategory === "ALL" ? ui.allEducationalGuides : selectedCategory}
                                            </h2>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                {ui.showingArticles(filteredArticles.length)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Active Filter Indicators */}
                                    {isFiltered && (
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {searchQuery && (
                                                <span className="inline-flex items-center gap-1.5 text-xs bg-orange-50 border border-orange-200 text-brand-orange px-3 py-1 rounded-xl font-medium">
                                                    <span>
                                                        {ui.searchLabel} <strong>{searchQuery}</strong>
                                                    </span>
                                                    <button
                                                        onClick={() => setSearchQuery("")}
                                                        className="hover:text-red-500 font-bold ml-1 cursor-pointer"
                                                    >
                                                        ✕
                                                    </button>
                                                </span>
                                            )}
                                            {selectedCategory !== "ALL" && (
                                                <span className="inline-flex items-center gap-1.5 text-xs bg-slate-100 border border-slate-200 text-slate-700 px-3 py-1 rounded-xl font-medium">
                                                    <span>{selectedCategory}</span>
                                                    <button
                                                        onClick={() => setSelectedCategory("ALL")}
                                                        className="hover:text-red-500 font-bold ml-1 cursor-pointer"
                                                    >
                                                        ✕
                                                    </button>
                                                </span>
                                            )}
                                            <button
                                                onClick={resetFilters}
                                                className="text-xs text-brand-orange hover:underline font-bold px-1 cursor-pointer"
                                            >
                                                {ui.clearAll}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Article Feed Cards Grid */}
                                {filteredArticles.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {filteredArticles.map((item) => (
                                            <a
                                                key={item.slug}
                                                href={localePath(locale, `knowledge-base/${item.slug}`)}
                                                className="group bg-white rounded-3xl p-6 sm:p-7 border border-slate-200/80 hover:border-brand-orange/60 shadow-xs hover:shadow-xl hover:shadow-brand-orange/10 transition-all duration-300 flex flex-col justify-between"
                                            >
                                                <div>
                                                    {/* Top Meta: Category & Reading Time */}
                                                    <div className="flex items-center justify-between gap-2 mb-4">
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-orange-50 text-brand-orange font-bold text-xs border border-orange-100/80">
                                                            {getCategoryIcon(item.category, false)}
                                                            <span>{item.category}</span>
                                                        </span>
                                                        <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                                                            <Clock size={13} />
                                                            <span>{item.readingTime}</span>
                                                        </div>
                                                    </div>

                                                    {/* Article Title */}
                                                    <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-3 group-hover:text-brand-orange transition-colors leading-snug">
                                                        {item.title}
                                                    </h3>

                                                    {/* Excerpt */}
                                                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed mb-5 line-clamp-3">
                                                        {item.excerpt}
                                                    </p>

                                                    {/* Key Highlight / Bullet Snippet */}
                                                    {item.keyTakeaways && item.keyTakeaways.length > 0 && (
                                                        <div className="mb-5 p-3 rounded-2xl bg-slate-50 border border-slate-100 group-hover:bg-orange-50/50 group-hover:border-orange-100 transition-colors">
                                                            <div className="flex items-start gap-2">
                                                                <CheckCircle2
                                                                    size={14}
                                                                    className="text-brand-orange mt-0.5 shrink-0"
                                                                />
                                                                <p className="text-xs text-slate-700 font-medium line-clamp-2 leading-relaxed">
                                                                    {item.keyTakeaways[0]}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Bottom Action / Footer */}
                                                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs mt-auto">
                                                    <div className="flex items-center gap-1.5 text-slate-400">
                                                        <Calendar size={13} />
                                                        <span>{item.datePublished}</span>
                                                    </div>

                                                    <span className="text-brand-orange font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
                                                        <span>{ui.readArticle}</span>
                                                        <ArrowUpRight
                                                            size={15}
                                                            className={`transition-transform duration-300 ${
                                                                isRtl
                                                                    ? "group-hover:-translate-x-1 group-hover:-translate-y-1"
                                                                    : "group-hover:translate-x-1 group-hover:-translate-y-1"
                                                            }`}
                                                        />
                                                    </span>
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    /* Empty State */
                                    <div className="bg-white rounded-3xl p-12 text-center border border-slate-200 shadow-sm">
                                        <div className="w-16 h-16 bg-orange-50 text-brand-orange rounded-2xl flex items-center justify-center mx-auto mb-5">
                                            <Search size={28} />
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-2">
                                            {ui.noArticlesFound}
                                        </h3>
                                        <p className="text-slate-500 text-sm mb-6 leading-relaxed max-w-md mx-auto">
                                            {ui.noArticlesBody}
                                        </p>
                                        <button
                                            onClick={resetFilters}
                                            className="inline-flex items-center gap-2 bg-brand-blue-gray text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-slate-800 transition-colors shadow-md cursor-pointer"
                                        >
                                            <RotateCcw size={15} />
                                            <span>{ui.clearFilters}</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Featured R&D Quote Banner */}
                <section className="pb-20 sm:pb-28 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-5xl mx-auto bg-brand-blue-gray p-10 sm:p-14 lg:p-18 rounded-[40px] text-white relative overflow-hidden text-center shadow-2xl">
                        <div className="absolute top-6 right-8 text-[140px] font-serif text-white/5 pointer-events-none select-none">
                            &quot;
                        </div>
                        <p className="text-2xl sm:text-3xl lg:text-4xl font-light leading-relaxed italic relative z-10 max-w-3xl mx-auto">
                            {ui.quote}
                        </p>
                        <div className="mt-8 sm:mt-10 inline-block bg-brand-orange text-white px-8 py-2.5 rounded-full font-bold text-sm sm:text-base shadow-lg shadow-brand-orange/30">
                            {ui.rndTeam}
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}
